import type { TourActionContext, TourDefinition } from './types';
import { getRouter } from '@/router/instance';
import { clickAnimated, delay, pulse, selectFirstOption } from '@/utils/tour/actions';
import {
  hasVisibleTarget,
  waitForCondition,
  waitForTourElement,
} from '@/utils/tour/waitForElement';
import { logger } from '@/utils/logger';

/**
 * Scripted playback demo for Signing with GAIA, driving the rebuilt pages
 * under `/signing`. The tour performs every real action itself (the user only
 * clicks Next/Back/Replay/exit): it creates a fresh wizard draft, types the
 * request name, selects the provider, attaches a document from the user's own
 * vault, adds a signer, draws a signature field — and stops at "Submit and
 * activate signature", the one irreversible action (it sends real signature
 * emails). The demo leaves no residue: its draft is cleaned up whether the
 * tour completes, is dismissed, or is abandoned.
 *
 * Every element the script touches is addressed by its `data-tour` key.
 * Nothing here reaches for a CSS class — the rebuild moved this UI once
 * already, and a demo that breaks on a restyle is a demo nobody trusts.
 * Naive-internal selectors (a select's trigger, its teleported menu) stay
 * behind the shared executors in `utils/tour/actions.ts`.
 */

/**
 * The wizard's own validation, read straight from the UI: Continue is only
 * enabled once the current step is complete. Guards the "Let's continue"
 * scenes so a failed fill skips them instead of showing a mismatched bubble.
 * The button exists on every step, and its submit variant on the activate
 * step never counts.
 */
function continueEnabled(): boolean {
  const button = document.querySelector<HTMLElement>('[data-tour="signing.continue"]');
  if (!button || button.getClientRects().length === 0 || button.hasAttribute('disabled')) {
    return false;
  }
  return !/submit|activate|send/i.test(button.textContent);
}

/**
 * Click the wizard's Continue — UNLESS it has become the irreversible
 * "Submit and activate signature" button. The shared button changes meaning on
 * the activate step; a Replay after advancing must never send a real signature
 * request. Guarded by what the button says, not by scene state.
 */
async function clickContinueUnlessSubmit(ctx: TourActionContext): Promise<void> {
  const button = await waitForTourElement('signing.continue');
  if (!button || ctx.isCancelled()) {
    return;
  }
  if (/submit|activate|send/i.test(button.textContent)) {
    logger.warn('[tour] continue button is the submit action — refusing to click it');
    return;
  }
  ctx.setHighlight(button);
  if (ctx.animate) {
    await delay(700);
  }
  await clickAnimated(button, ctx);
}

/**
 * Pick the FIRST provider on offer — the list depends on what the org has
 * (GAIA's provider, a connected Youtrust account, or both), so the demo takes
 * whatever is there rather than a hardcoded choice.
 */
async function pickFirstProvider(ctx: TourActionContext): Promise<void> {
  const select = await waitForTourElement('signing.init-provider');
  if (!select || ctx.isCancelled()) {
    return;
  }
  ctx.setHighlight(select);
  if (ctx.animate) {
    await delay(500);
  }
  await selectFirstOption(select, ctx);
}

/** Switch the documents step to the vault source. */
async function openFileTab(ctx: TourActionContext): Promise<void> {
  const tab = await waitForTourElement('signing.doc-tab-file');
  if (!tab || ctx.isCancelled()) {
    return;
  }
  ctx.setHighlight(tab);
  await clickAnimated(tab, ctx);
}

/**
 * Attach a document from the user's own vault: the first pickable plain file
 * in the vault browser's opening folder (files the provider can't accept
 * render disabled). No demo lookups — whatever data the org has is what the
 * demo uses. The tour doesn't navigate: with no plain file at that level it
 * skips the step.
 */
async function selectFirstVaultFile(ctx: TourActionContext): Promise<void> {
  const list = await waitForTourElement('signing.file-browser');
  if (!list || ctx.isCancelled()) {
    return;
  }

  // On replays the attached file is already ticked — just show it again.
  const selectedRow = list.querySelector<HTMLElement>(
    '[data-tour="vault.file-row"][data-selected="true"]'
  );
  if (selectedRow) {
    selectedRow.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
    ctx.setHighlight(selectedRow);
    if (ctx.animate) {
      await delay(700);
    }
    pulse(selectedRow);
    if (ctx.animate) {
      await delay(900);
    }
    return;
  }

  // The list fills asynchronously after the tab opens.
  const row = await waitForCondition(() =>
    list.querySelector<HTMLElement>('[data-tour="vault.file-row"]:not([disabled])')
  );
  if (!row || ctx.isCancelled()) {
    logger.warn('[tour] no pickable file found in the vault — skipping selection');
    return;
  }

  row.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(row);
  if (ctx.animate) {
    await delay(700);
  }
  await clickAnimated(row, ctx);
  if (ctx.animate) {
    // A long beat so the tick registers before the bubble moves on.
    await delay(900);
  }
}

// Whether the demo actually got a signer in — set synchronously by
// addDemoSigner, so the signee-card scene skips instantly when no qualifying
// person existed instead of waiting out its target.
let signerAdded = false;

/**
 * Add a demo signer end to end: open the picker, tick the first person who can
 * actually sign (the wizard needs an email, so pick the first row whose text
 * contains an email address), confirm. Idempotent — replaying with a signer
 * already added does nothing instead of stacking duplicates.
 */
async function addDemoSigner(ctx: TourActionContext): Promise<void> {
  signerAdded = false;

  // A visible signee card means a recipient is already in.
  if (hasVisibleTarget('signing.signee-card')) {
    signerAdded = true;
    return;
  }

  const addButton = await waitForTourElement('signing.add-signer');
  if (!addButton || ctx.isCancelled()) {
    return;
  }
  ctx.setHighlight(addButton);
  if (ctx.animate) {
    await delay(700);
  }
  await clickAnimated(addButton, ctx);

  // The picker's confirm button appearing is the modal being open.
  const confirmButton = await waitForTourElement('signing.picker-add');
  if (!confirmButton || ctx.isCancelled()) {
    return;
  }

  const row = await waitForCondition(() => {
    const rows = document.querySelectorAll<HTMLElement>('[data-tour="signing.picker-row"]');
    return (
      [...rows].find(
        candidate =>
          /\S+@\S+\.\S+/.test(candidate.textContent) && !candidate.hasAttribute('disabled')
      ) ?? null
    );
  });
  if (ctx.isCancelled()) {
    return;
  }
  if (!row) {
    // Nobody qualifies. Close the picker so the scene never ends with a
    // dangling modal.
    logger.warn('[tour] no recipient with an email found in the picker — closing it');
    const cancel = [...document.querySelectorAll<HTMLElement>('button')].find(
      button => button.textContent.trim() === 'Cancel'
    );
    if (cancel) {
      await clickAnimated(cancel, ctx);
    }
    return;
  }

  row.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(row);
  if (ctx.animate) {
    await delay(800);
  }
  await clickAnimated(row, ctx);
  if (ctx.animate) {
    await delay(600);
  }
  if (ctx.isCancelled()) {
    return;
  }

  ctx.setHighlight(confirmButton);
  if (ctx.animate) {
    await delay(500);
  }
  await clickAnimated(confirmButton, ctx);
  signerAdded = !ctx.isCancelled();
}

/**
 * Draw a signature field on the page: activate the first signer, arm the
 * Signature palette entry, then drag a box with synthetic mouse events (the
 * canvas reads clientX/Y against its own bounding rect).
 */
async function placeSignatureField(ctx: TourActionContext): Promise<void> {
  const signerChip = await waitForTourElement('signing.field-signer-chip');
  if (!signerChip || ctx.isCancelled()) {
    logger.warn('[tour] no signer available for field placement — skipping');
    return;
  }
  ctx.setHighlight(signerChip);
  if (ctx.animate) {
    await delay(600);
  }
  // Clicking an already-active signer would deactivate it, so the chip is only
  // clicked when the Signature entry is still inert — which is exactly the
  // state that says no signer is active.
  const signatureEntry = await waitForTourElement('signing.field-palette-signature');
  if (!signatureEntry || ctx.isCancelled()) {
    return;
  }

  await clickAnimated(signerChip, ctx);
  if (ctx.animate) {
    await delay(400);
  }
  if (ctx.isCancelled()) {
    return;
  }

  ctx.setHighlight(signatureEntry);
  if (ctx.animate) {
    await delay(500);
  }
  await clickAnimated(signatureEntry, ctx);
  if (ctx.animate) {
    await delay(400);
  }
  if (ctx.isCancelled()) {
    return;
  }

  const overlay = await waitForTourElement('signing.field-overlay');
  if (!overlay || ctx.isCancelled()) {
    logger.warn('[tour] field overlay not found — skipping field placement');
    return;
  }

  // The field goes at the BOTTOM right, where signatures belong, so the
  // document's bottom is scrolled into view first — on a tall page it sits
  // below the fold and the draw must stay visible.
  overlay.scrollIntoView({ block: 'end', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(overlay);
  if (ctx.animate) {
    // Smooth scroll needs to settle before the overlay's rect is measured.
    await delay(800);
  }

  const rect = overlay.getBoundingClientRect();
  const FIELD_WIDTH = 190;
  const FIELD_HEIGHT = 64;
  const PADDING = 24;
  const endX = rect.right - PADDING;
  const startX = Math.max(rect.left + 8, endX - FIELD_WIDTH);
  const endY = rect.bottom - PADDING;
  const startY = Math.max(rect.top + 8, endY - FIELD_HEIGHT);

  const mouse = (type: string, target: EventTarget, x: number, y: number): void => {
    target.dispatchEvent(
      new MouseEvent(type, {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: x,
        clientY: y,
        button: 0,
      })
    );
  };

  mouse('mousedown', overlay, startX, startY);
  // The canvas tracks drawing via window-level mousemove/mouseup.
  const steps = ctx.animate ? 12 : 1;
  for (let step = 1; step <= steps; step++) {
    if (ctx.isCancelled()) {
      // Never leave the canvas mid-draw with window listeners attached.
      mouse('mouseup', window, endX, endY);
      return;
    }
    const progress = step / steps;
    mouse(
      'mousemove',
      window,
      startX + (endX - startX) * progress,
      startY + (endY - startY) * progress
    );
    if (ctx.animate) {
      await delay(45);
    }
  }
  mouse('mouseup', window, endX, endY);
}

export const signingTour: TourDefinition = {
  key: 'signing',
  title: 'Signing with GAIA',
  description: 'Watch a signature request get prepared end to end.',
  entryRoute: 'signing-requests',
  requiredPermission: 'can_use_signing',
  // Finished watching: land back on the requests list. The wizard never saves
  // during playback, so the demo leaves no draft behind.
  onComplete: async () => {
    try {
      await getRouter().push({ name: 'signing-requests' });
    } catch {
      // Navigation declined (e.g. a guard) — completing the tour still stands.
    }
  },
  steps: [
    {
      id: 'requests-overview',
      route: 'signing-requests',
      target: 'signing.requests-table',
      placement: 'top',
      title: 'Your signature requests',
      body:
        'Every request lives here: who needs to sign, the current status, ' +
        'and how far along it is. Let’s prepare one together.',
    },
    {
      id: 'create-request',
      route: 'signing-requests',
      target: 'signing.create-request',
      placement: 'bottom-end',
      title: 'Start a request',
      body: 'Let’s create one together.',
      interactive: true,
      // The button is disabled when the org has no signing provider — the
      // scripted click would do nothing, so skip the demo in that case.
      prerequisite: () =>
        !document.querySelector('[data-tour="signing.create-request"]')?.hasAttribute('disabled'),
      actions: [{ kind: 'click', target: 'signing.create-request' }],
    },
    {
      id: 'documents',
      target: 'signing.upload-tabs',
      placement: 'top',
      title: '1 · Attach the documents',
      body:
        'Pick files from a contract, search the vault, or upload one. I will pick the ' +
        'first vault file.',
      actions: [
        { kind: 'custom', run: openFileTab },
        { kind: 'custom', run: selectFirstVaultFile },
      ],
    },
    {
      id: 'documents-selected',
      target: 'signing.selected-doc',
      placement: 'top',
      title: 'What each file is for',
      body:
        'Signable documents get the signature fields; anything marked as an ' +
        'attachment just rides along.',
      prerequisite: () => hasVisibleTarget('signing.selected-doc'),
    },
    {
      id: 'initialize',
      route: 'signing-request-new',
      target: 'signing.init-name',
      placement: 'bottom',
      title: 'Name it and pick a provider',
      body:
        'The request is named after the document — change it if your team ' +
        'knows it by another name. I will type one in.',
      actions: [
        {
          kind: 'type',
          target: 'signing.init-name',
          text: 'Acme Partnership NDA (demo)',
          charMs: 28,
        },
      ],
    },
    {
      id: 'providers',
      target: 'signing.init-provider',
      placement: 'bottom',
      title: 'Choose who runs the signature',
      body:
        'Sign with GAIA’s built-in provider, or with your own Youtrust ' +
        'account once its integration is connected in settings. I will pick ' +
        'the first one available.',
      actions: [{ kind: 'custom', run: pickFirstProvider }],
    },
    {
      id: 'documents-continue',
      target: 'signing.continue',
      placement: 'top-end',
      title: 'Ready for recipients',
      body: 'Let’s continue.',
      interactive: true,
      prerequisite: continueEnabled,
      actions: [{ kind: 'custom', run: clickContinueUnlessSubmit }],
    },
    {
      id: 'recipients',
      target: 'signing.add-signer',
      placement: 'top',
      title: '2 · Add who signs',
      body:
        'Signers, approvers and followers come from your entities. Press ' +
        'Next and I will add a demo person as the signer.',
      actions: [{ kind: 'custom', run: addDemoSigner }],
      backActions: [{ kind: 'click', target: 'signing.wizard-back' }],
    },
    {
      id: 'recipients-added',
      // The flag beats a bare DOM check: the card renders once addDemoSigner
      // confirms the picker. Its own visibility covers replays that arrive
      // with a signer already present.
      prerequisite: () => signerAdded || hasVisibleTarget('signing.signee-card'),
      target: 'signing.signee-card',
      placement: 'bottom',
      title: 'Your signer is in',
      body:
        'Each recipient shows who they are and how they sign. Switch signers ' +
        'to In order to choose who signs first; the ⋯ menu edits, moves or removes them.',
    },
    {
      id: 'recipients-continue',
      target: 'signing.continue',
      placement: 'top-end',
      title: 'Signer added',
      body: 'Let’s continue.',
      interactive: true,
      prerequisite: continueEnabled,
      actions: [{ kind: 'custom', run: clickContinueUnlessSubmit }],
    },
    {
      id: 'fields',
      target: 'signing.steps',
      placement: 'right',
      title: '3 · Place the signature field',
      body:
        'Drag or draw fields straight onto the document, one per signer. I ' +
        'will draw one at the bottom right.',
      prerequisite: () => hasVisibleTarget('signing.field-overlay'),
      actions: [{ kind: 'custom', run: placeSignatureField }],
      backActions: [{ kind: 'click', target: 'signing.wizard-back' }],
    },
    {
      id: 'field-adjust',
      target: 'signing.field-box',
      placement: 'left',
      title: 'Put it where you want',
      body:
        'Drag the field anywhere on the page, resize it from the corner, or ' +
        'click it to change its signer. Fields are yours to arrange.',
      prerequisite: () => hasVisibleTarget('signing.field-box'),
    },
    {
      id: 'fields-continue',
      target: 'signing.continue',
      placement: 'top-end',
      title: 'Field placed',
      body: 'Let’s continue.',
      interactive: true,
      prerequisite: continueEnabled,
      actions: [{ kind: 'custom', run: clickContinueUnlessSubmit }],
    },
    {
      id: 'activate',
      target: 'signing.activate-overview',
      placement: 'top',
      title: '4 · Review and send',
      body:
        'Everything is prepared. "Send for signing" sends it to ' +
        'each signer. That part is yours.',
      backActions: [{ kind: 'click', target: 'signing.wizard-back' }],
    },
    {
      id: 'finale',
      title: 'That is Signing with GAIA',
      body:
        'Name the request, attach documents, add signers, place the fields, ' +
        'and send. Track every request and its signatures from the requests ' +
        'page. This demo draft is cleaned up automatically.',
    },
  ],
};
